// ============================================
// Prompt Optimizer Service
// Analyzes test run failures and suggests
// specific prompt template improvements.
// Uses Claude (via claudeProxy) for analysis.
// ============================================

import type { Message, CheckItem, CheckResult, NurseTraitProfile } from '../../types/patientJourney';
import type { PromptAnalysis, PromptSuggestion, TraitAdjustment } from '../../types/promptConfig';
import { claudeGenerate, claudeChat } from '../ai/providers/claude';

const OPTIMIZER_MODEL = 'claude-sonnet-4-5-20250929';

/**
 * Analyze a completed test run and suggest prompt improvements.
 * Feeds the conversation log, check results, and current prompt rules
 * to Claude, which identifies failure patterns and returns targeted edits.
 */
export async function analyzeRunAndSuggest(
  runId: string,
  conversationLog: Message[],
  checkResults: CheckResult[],
  validationChecks: CheckItem[],
  currentPromptRules: string,
  promptVersion: number,
  userGuidance?: string,
): Promise<PromptAnalysis> {
  // Format conversation for analysis
  const conversationText = conversationLog
    .map((m) => `[${m.role === 'ai_nurse' ? 'NURSE' : 'PATIENT'}]: ${m.content}`)
    .join('\n\n');

  // Format check results with their descriptions
  const failedChecks = checkResults
    .filter((cr) => cr.pass === false)
    .map((cr) => {
      const checkDef = validationChecks.find((c) => c.checkId === cr.checkId);
      return {
        checkId: cr.checkId,
        check: checkDef?.check || 'Unknown check',
        category: checkDef?.category || 'unknown',
        severity: checkDef?.severity || 'unknown',
        reasoning: cr.notes || 'No reasoning provided',
      };
    });

  const passedChecks = checkResults
    .filter((cr) => cr.pass === true)
    .map((cr) => {
      const checkDef = validationChecks.find((c) => c.checkId === cr.checkId);
      return {
        checkId: cr.checkId,
        check: checkDef?.check || 'Unknown check',
      };
    });

  if (failedChecks.length === 0) {
    return {
      analysisId: `analysis_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      runId,
      promptVersion,
      createdAt: new Date().toISOString(),
      failurePatterns: 'All checks passed. No improvements needed.',
      suggestions: [],
      newVersionGenerated: false,
    };
  }

  const passRate = checkResults.length > 0
    ? Math.round((passedChecks.length / checkResults.length) * 100)
    : 0;

  const analyzerPrompt = `You are a prompt engineering optimizer for an AI nurse chatbot called the Hemologix Intake Nurse. The nurse runs on Gemini 2.5 Flash.

Your job: analyze test run failures and suggest SPECIFIC, MINIMAL text edits to the nurse's system prompt that would fix the failures WITHOUT breaking what's already passing.

CURRENT PASS RATE: ${passRate}% (${passedChecks.length}/${checkResults.length})

PASSING CHECKS (do NOT break these):
${passedChecks.map((c) => `- ${c.checkId}: ${c.check}`).join('\n')}

FAILED CHECKS (fix these):
${failedChecks.map((c) => `- ${c.checkId} [${c.severity}] [${c.category}]: ${c.check}\n  SCORER REASONING: ${c.reasoning}`).join('\n\n')}

FULL CONVERSATION:
${conversationText}

CURRENT PROMPT RULES (the part we can edit):
${currentPromptRules}

${userGuidance ? `HUMAN GUIDANCE (from the product owner — prioritize this feedback):\n${userGuidance}\n\n` : ''}INSTRUCTIONS:
1. Identify the ROOT CAUSE patterns behind the failures. Why did the nurse behave this way?
2. For each failure pattern, suggest a SPECIFIC text edit to the prompt rules.
3. Each suggestion must include:
   - Which section to edit (e.g., "VOICE RULES", "CLINICAL RULES", "CONVERSATION FLOW")
   - The exact current text to find
   - The exact replacement text
   - Brief reasoning for the change
   - Which check IDs this fixes
4. Make MINIMAL changes. Do not rewrite sections that are working.
5. Consider that this is Gemini 2.5 Flash — it tends to: use markdown even when told not to, write long responses, and ignore coverage rules unless they're very explicit.

Respond in this EXACT JSON format (no other text):
{
  "failurePatterns": "Brief summary of why checks are failing",
  "suggestions": [
    {
      "section": "VOICE RULES",
      "currentText": "exact text to find in the current rules",
      "suggestedText": "exact replacement text",
      "reasoning": "why this change fixes the failure",
      "addressesChecks": ["CK-001", "CTX-SIM-01"]
    }
  ]
}`;

  const response = await claudeGenerate(
    OPTIMIZER_MODEL,
    'You are a prompt engineering expert. Return ONLY valid JSON. No markdown code fences, no explanation outside the JSON.',
    analyzerPrompt,
    0,
    4096,
  );

  // Parse the response
  let parsed: { failurePatterns: string; suggestions: Array<{
    section: string;
    currentText: string;
    suggestedText: string;
    reasoning: string;
    addressesChecks: string[];
  }> };

  try {
    // Strip markdown code fences if present
    const cleaned = response.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    parsed = JSON.parse(cleaned);
  } catch {
    // If JSON parsing fails, return the raw text as analysis
    return {
      analysisId: `analysis_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      runId,
      promptVersion,
      createdAt: new Date().toISOString(),
      failurePatterns: response,
      suggestions: [],
      newVersionGenerated: false,
    };
  }

  const suggestions: PromptSuggestion[] = parsed.suggestions.map((s, i) => ({
    id: `sug_${Date.now()}_${i}`,
    section: s.section,
    currentText: s.currentText,
    suggestedText: s.suggestedText,
    reasoning: s.reasoning,
    addressesChecks: s.addressesChecks || [],
    status: 'pending' as const,
  }));

  return {
    analysisId: `analysis_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    runId,
    promptVersion,
    createdAt: new Date().toISOString(),
    failurePatterns: parsed.failurePatterns,
    suggestions,
    newVersionGenerated: false,
  };
}

/**
 * Multi-turn conversational optimizer. Instead of one-shot analysis,
 * the user chats with the optimizer to refine prompt suggestions.
 * Context about the run (conversation, checks, current rules) is
 * baked into the system prompt so every reply has full awareness.
 */
export async function chatWithOptimizer(
  chatHistory: { role: 'user' | 'assistant'; content: string }[],
  userMessage: string,
  runContext: {
    conversationLog: Message[];
    checkResults: CheckResult[];
    validationChecks: CheckItem[];
    currentPromptRules: string;
    passRate: number;
    traitProfile?: NurseTraitProfile;
  },
): Promise<{ reply: string; suggestions: PromptSuggestion[]; traitAdjustments: TraitAdjustment[] }> {
  const conversationText = runContext.conversationLog
    .map((m) => `[${m.role === 'ai_nurse' ? 'NURSE' : 'PATIENT'}]: ${m.content}`)
    .join('\n\n');

  const failedChecks = runContext.checkResults
    .filter((cr) => cr.pass === false)
    .map((cr) => {
      const checkDef = runContext.validationChecks.find((c) => c.checkId === cr.checkId);
      return {
        checkId: cr.checkId,
        check: checkDef?.check || 'Unknown check',
        category: checkDef?.category || 'unknown',
        severity: checkDef?.severity || 'unknown',
        reasoning: cr.notes || 'No reasoning provided',
      };
    });

  const passedChecks = runContext.checkResults
    .filter((cr) => cr.pass === true)
    .map((cr) => {
      const checkDef = runContext.validationChecks.find((c) => c.checkId === cr.checkId);
      return { checkId: cr.checkId, check: checkDef?.check || 'Unknown check' };
    });

  // Build trait profile context
  const traitCtx = runContext.traitProfile
    ? `\nCURRENT NURSE TRAIT SETTINGS (0-100 sliders that control behavioral intensity):
  Empathy: ${runContext.traitProfile.empathy} (Guide register warmth — low=clinical, high=warm)
  Data Density: ${runContext.traitProfile.dataDensity} (biomarker refs per response — low=summary, high=dense)
  Explanation Depth: ${runContext.traitProfile.explanationDepth} (how thoroughly ranges are explained — low=brief, high=thorough)
  Product Timing: ${runContext.traitProfile.productTiming} (when to introduce products — low=early, high=late)
  Verification: ${runContext.traitProfile.verificationFreq} (follow-up question frequency — low=rare, high=frequent)
  Urgency: ${runContext.traitProfile.urgencyCalibration} (alarm level for findings — low=calm, high=urgent)\n`
    : '';

  const systemPrompt = `You are a prompt engineering coach for the Hemologix Intake Nurse (runs on Gemini 2.5 Flash).

You have FULL CONTEXT of the latest test run below. The user is a non-technical product owner who thinks in business terms — they'll say things like "the nurse is too pushy" or "mention DHEA-S earlier" rather than prompt engineering jargon. Your job is to translate their plain-English feedback into specific changes — both prompt text edits AND trait slider adjustments.

CURRENT PASS RATE: ${runContext.passRate}% (${passedChecks.length}/${runContext.checkResults.length})

PASSING CHECKS (do NOT break these):
${passedChecks.map((c) => `- ${c.checkId}: ${c.check}`).join('\n')}

FAILED CHECKS (fix these):
${failedChecks.map((c) => `- ${c.checkId} [${c.severity}] [${c.category}]: ${c.check}\n  SCORER REASONING: ${c.reasoning}`).join('\n\n')}
${traitCtx}
FULL CONVERSATION FROM TEST RUN:
${conversationText}

CURRENT PROMPT RULES (the part we can edit):
${runContext.currentPromptRules}

INSTRUCTIONS:
1. Respond naturally and conversationally. Explain what you see and what you'd change.
2. You can suggest TWO types of changes:
   a) PROMPT EDITS — specific text find-and-replace in the prompt rules
   b) TRAIT ADJUSTMENTS — slider value changes (e.g., empathy 70→85, dataDensity 60→45)
3. When proposing prompt edits, include them at the END of your response in this format:

---SUGGESTIONS---
[{"section":"VOICE RULES","currentText":"exact text to find","suggestedText":"replacement text","reasoning":"why this fixes it","addressesChecks":["CK-001"]}]

4. When proposing trait slider changes, include them AFTER suggestions in this format:

---TRAITS---
[{"trait":"empathy","from":70,"to":85,"reasoning":"nurse was too clinical in Phase 1 data presentation"}]

Valid trait keys: empathy, dataDensity, explanationDepth, productTiming, verificationFreq, urgencyCalibration (all 0-100).
5. You can include both ---SUGGESTIONS--- and ---TRAITS---, just one, or neither.
6. Make MINIMAL changes — don't rewrite working sections.
7. Remember: Gemini 2.5 Flash tends to use markdown, write long responses, and ignore rules unless very explicit.
8. If the user asks you to change something that would break a passing check, warn them.`;

  const response = await claudeChat(
    OPTIMIZER_MODEL,
    systemPrompt,
    chatHistory,
    userMessage,
    0.3,
    4096,
  );

  // Split response on ---SUGGESTIONS--- and ---TRAITS--- markers
  const sugMarker = '---SUGGESTIONS---';
  const traitMarker = '---TRAITS---';

  let reply = response;
  let suggestions: PromptSuggestion[] = [];
  let traitAdjustments: TraitAdjustment[] = [];

  // Extract suggestions block
  const sugIdx = response.indexOf(sugMarker);
  if (sugIdx >= 0) {
    reply = response.slice(0, sugIdx).trim();
    const afterSug = response.slice(sugIdx + sugMarker.length);
    // The JSON runs until ---TRAITS--- or end of string
    const traitIdxInAfter = afterSug.indexOf(traitMarker);
    const sugJsonStr = traitIdxInAfter >= 0
      ? afterSug.slice(0, traitIdxInAfter).trim()
      : afterSug.trim();

    try {
      const cleaned = sugJsonStr.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      const parsed: Array<{
        section: string;
        currentText: string;
        suggestedText: string;
        reasoning: string;
        addressesChecks: string[];
      }> = JSON.parse(cleaned);

      suggestions = parsed.map((s, i) => ({
        id: `sug_${Date.now()}_${i}`,
        section: s.section,
        currentText: s.currentText,
        suggestedText: s.suggestedText,
        reasoning: s.reasoning,
        addressesChecks: s.addressesChecks || [],
        status: 'pending' as const,
      }));
    } catch {
      // JSON parsing failed — keep reply as-is
      if (sugIdx >= 0) reply = response.slice(0, sugIdx).trim();
    }
  }

  // Extract trait adjustments block
  const traitIdx = response.indexOf(traitMarker);
  if (traitIdx >= 0) {
    // If no suggestions marker, reply is everything before traits
    if (sugIdx < 0) {
      reply = response.slice(0, traitIdx).trim();
    }
    const traitJsonStr = response.slice(traitIdx + traitMarker.length).trim();

    try {
      const cleaned = traitJsonStr.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      const parsed: Array<{
        trait: string;
        from: number;
        to: number;
        reasoning: string;
      }> = JSON.parse(cleaned);

      traitAdjustments = parsed.filter((t) =>
        ['empathy', 'dataDensity', 'explanationDepth', 'productTiming', 'verificationFreq', 'urgencyCalibration'].includes(t.trait),
      );
    } catch {
      // Trait JSON parsing failed — skip
    }
  }

  return { reply, suggestions, traitAdjustments };
}

/**
 * Apply accepted suggestions to a prompt template to create a new version.
 * Performs find-and-replace for each accepted suggestion.
 */
export function applyPromptSuggestions(
  currentTemplate: string,
  suggestions: PromptSuggestion[],
): string {
  let updated = currentTemplate;

  for (const suggestion of suggestions) {
    if (suggestion.status !== 'accepted') continue;
    if (updated.includes(suggestion.currentText)) {
      updated = updated.replace(suggestion.currentText, suggestion.suggestedText);
    }
  }

  return updated;
}

/**
 * Teaching Extractor — analyzes a demonstration session where the HUMAN
 * played the nurse, and extracts positive patterns into prompt suggestions.
 * Unlike chatWithOptimizer (which finds what's WRONG), this finds what's RIGHT.
 */
export async function extractTeachingInsights(
  chatHistory: { role: 'user' | 'assistant'; content: string }[],
  userMessage: string,
  teachContext: {
    conversationLog: Message[];
    currentPromptRules: string;
    personaName: string;
    personaContext: string;
  },
): Promise<{ reply: string; suggestions: PromptSuggestion[] }> {
  const conversationText = teachContext.conversationLog
    .map((m) => `[${m.role === 'ai_nurse' ? 'HUMAN NURSE' : 'AI PATIENT'}]: ${m.content}`)
    .join('\n\n');

  const systemPrompt = `You are a prompt engineering coach analyzing a TEACHING SESSION.

A human product owner just demonstrated how the Hemologix intake nurse SHOULD behave by playing the nurse role with an AI patient. Your job is to extract what they did well and turn it into specific, concrete prompt rules.

The human is NOT an AI — they are the product owner showing how the nurse should sound, respond, and build trust. They know their customer and their product. Learn from them.

PATIENT PERSONA: ${teachContext.personaName}
PATIENT CONTEXT: ${teachContext.personaContext}

CURRENT NURSE PROMPT RULES (for comparison — what's already in the prompt):
${teachContext.currentPromptRules}

FULL TEACHING CONVERSATION (HUMAN = nurse demonstrating, AI = patient roleplaying):
${conversationText}

INSTRUCTIONS:
1. Identify 3-5 specific things the human did well that the current prompt doesn't capture or doesn't emphasize enough.
2. For each insight, propose a specific prompt rule addition or edit.
3. Focus on:
   - Tone and phrasing choices (what words did they choose?)
   - Empathy timing (when did they validate vs. ask questions?)
   - Objection handling (how did they respond to pushback?)
   - Information sequencing (what did they say first, second, third?)
   - Trust-building techniques (what made the patient open up?)
4. Quote the human's exact words when they demonstrate a good pattern.
5. Note where the patient responded positively and what triggered it.
6. Compare against the current prompt rules — what's MISSING or should be STRONGER?
7. Be specific. "Be more empathetic" is useless. "Start with 'That sounds exhausting' before any clinical questions" is useful.

Respond conversationally — explain what you noticed and why it matters. Then include concrete edits.

When proposing prompt edits, include them at the END of your response in this exact format:

---SUGGESTIONS---
[{"section":"VOICE RULES","currentText":"exact text to find","suggestedText":"replacement text","reasoning":"why this addition captures what the human demonstrated","addressesChecks":[]}]

Only include the ---SUGGESTIONS--- block when you have concrete edits to propose.
If the current text doesn't exist yet (new rule to add), set currentText to the location marker where it should be inserted (e.g. the last line of that section) and suggestedText to that line + the new rule.`;

  const response = await claudeChat(
    OPTIMIZER_MODEL,
    systemPrompt,
    chatHistory,
    userMessage,
    0.3,
    4096,
  );

  // Split response on ---SUGGESTIONS--- marker (same pattern as chatWithOptimizer)
  const marker = '---SUGGESTIONS---';
  const markerIdx = response.indexOf(marker);

  let reply: string;
  let suggestions: PromptSuggestion[] = [];

  if (markerIdx >= 0) {
    reply = response.slice(0, markerIdx).trim();
    const jsonStr = response.slice(markerIdx + marker.length).trim();

    try {
      const cleaned = jsonStr.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      const parsed: Array<{
        section: string;
        currentText: string;
        suggestedText: string;
        reasoning: string;
        addressesChecks: string[];
      }> = JSON.parse(cleaned);

      suggestions = parsed.map((s, i) => ({
        id: `teach_${Date.now()}_${i}`,
        section: s.section,
        currentText: s.currentText,
        suggestedText: s.suggestedText,
        reasoning: s.reasoning,
        addressesChecks: s.addressesChecks || [],
        status: 'pending' as const,
      }));
    } catch {
      reply = response;
    }
  } else {
    reply = response;
  }

  return { reply, suggestions };
}
